import {
  BoundMultiSelectField,
  Button,
  ModalBody,
  ModalFooter,
  ModalHeader,
  SubmitButton,
  useModal,
} from "@homebound/beam";
import { ObjectConfig, useFormState } from "@homebound/form-state";
import { InputMaybe } from "src/generated/graphqlTypes";
import {
  useAddSlotModal_PotentialPlaceholdersQuery,
  useAddSlotModal_SelectedPlaceholdersLazyQuery,
} from "src/routes/libraries/design-catalog/components/AddSlotModal.generated";
import { EditPersonalizationOptionPage_Slot_TakeoffLineItemFragment } from "src/routes/libraries/design-catalog/EditPersonalizationOptionPage.generated";
import { isDefined } from "src/utils";
import { required } from "src/utils/formState";

type AddSlotModalProps = {
  designPackageId: string;
  designPackageVersionId: string;
  onAddSlots: (placeholders: EditPersonalizationOptionPage_Slot_TakeoffLineItemFragment[]) => void;
  existingPlaceholderIds: string[];
  /** If adding to a product option, the requirement we must match; package options can add any slot. */
  materialVariantId: string | undefined;
};

export function AddSlotModal(props: AddSlotModalProps) {
  const { designPackageId, designPackageVersionId, onAddSlots, existingPlaceholderIds, materialVariantId } = props;
  const { closeModal } = useModal();

  const { data } = useAddSlotModal_PotentialPlaceholdersQuery({
    variables: { designPackageId, versionId: designPackageVersionId },
  });
  const [loadSelected] = useAddSlotModal_SelectedPlaceholdersLazyQuery();
  const formState = useFormState({ config: formConfig });

  const options =
    data?.takeoffLineItemsPage.items.filter(
      (tli) =>
        // Filter out existing placeholders
        !existingPlaceholderIds.includes(tli.id) &&
        // Filter to only slots for the specified material variant--could push this to the backend?
        (!isDefined(materialVariantId) || materialVariantId === tli.materialVariant?.id),
    ) ?? [];

  return (
    <>
      <ModalHeader>Add Slots</ModalHeader>
      <ModalBody>
        <BoundMultiSelectField
          labelStyle="hidden"
          label="Select a slot"
          field={formState.placeholderIds}
          options={options.map((o) => ({ id: o.id, name: `${o.name} - ${o.location.displayLocationPath}` }))}
          disabledOptions={options
            .filter((o) => isDefined(o.personalizationShadowPlaceholder))
            .map((o) => ({ value: o.id, reason: "This slot is already covered by a personalization option group" }))}
        />
      </ModalBody>
      <ModalFooter>
        <Button variant="quaternary" label="Cancel" onClick={closeModal} />
        <SubmitButton
          form={formState}
          label="Add Slots"
          onClick={async () => {
            const tliIds = formState.placeholderIds.value;
            if (tliIds && tliIds.nonEmpty) {
              const result = await loadSelected({ variables: { designPackageId, tliIds } });
              onAddSlots(result.data?.takeoffLineItemsPage.items!);
            }
            closeModal();
          }}
        />
      </ModalFooter>
    </>
  );
}

type FormValue = { placeholderIds: InputMaybe<string[]> };

const formConfig: ObjectConfig<FormValue> = { placeholderIds: { type: "value", rules: [required] } };
